/**
 * Dashboard Analítico v2 — VERSÃO ANTERIOR, mantida para comparação.
 *
 * Congelada no commit e2f098b. Fica acessível pelo alternador em
 * AnalyticsSwitch.jsx enquanto a v3 é avaliada. Não evoluir este arquivo:
 * corrigir aqui e não na v3 faz as duas divergirem. Quando a v3 estiver
 * aprovada, apagar este arquivo, styles/analytics-grid.css e o alternador.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import SecureClientSelector from './SecureClientSelector';
import { useAuth } from '../App';
import { formatDateOrDash } from '../utils/dates';
import { getStatusLabel } from '../utils/status';
import { Filter } from 'lucide-react';
import '../styles/analytics-grid.css';
import { displayProtocolTag } from '../utils/protocol';

const AnalyticsLegacyV2 = () => {
  const { user } = useAuth();
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para interatividade
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedClient, setSelectedClient] = useState('all'); // Novo filtro de cliente
  const [selectedFilters, setSelectedFilters] = useState({});

  // Paleta de 20 cores profissional para tipos de denúncia
  const tipoColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#14b8a6', '#f97316', '#ec4899', '#6366f1', '#84cc16',
    '#f43f5e', '#06b6d4', '#8b5cf6', '#f59e0b', '#10b981',
    '#ef4444', '#3b82f6', '#14b8a6', '#f97316', '#ec4899'
  ];

  // Paleta de cores profissional para outros gráficos
  const colors = {
    primary: ['#3b82f6', '#1d4ed8', '#1e40af'],
    secondary: ['#10b981', '#059669', '#047857'],
    accent: ['#f59e0b', '#d97706', '#b45309'],
    danger: ['#ef4444', '#dc2626', '#b91c1c'],
    purple: ['#8b5cf6', '#7c3aed', '#6d28d9'],
    teal: ['#14b8a6', '#0d9488', '#0f766e'],
    pink: ['#ec4899', '#db2777', '#be185d'],
    orange: ['#f97316', '#ea580c', '#c2410c']
  };

  const animationConfig = {
    isAnimationActive: false, // Desabilitado temporariamente para debug
    animationDuration: 450,
    animationEasing: 'ease-out'
  };

  const parseResponses = (responses) => {
    if (!responses) return {};
    if (typeof responses === 'object') return responses;
    try {
      return JSON.parse(responses);
    } catch (error) {
      console.warn('Não foi possível converter responses para objeto:', error);
      return {};
    }
  };

  // Conclusão (Grupo I) - desfecho da apuração da denúncia.
  // O banco mistura, nas colunas status_conclusao/conclusao, desfechos reais
  // (procedente, parcialmente procedente, improcedente, inconclusivo, fora do escopo)
  // com status de workflow e texto livre (Concluída, Em andamento, Teste, etc.).
  // Aqui reconhecemos APENAS os 5 desfechos canônicos (correspondência tolerante a
  // caixa/acento/variantes), checando ambas as colunas + admin_notes; tudo o que não
  // for um desfecho reconhecido (inclusive vazio/workflow/lixo) vira "Não informado".
  // Definidas no escopo do componente (não dentro de processRealData) para que o
  // filtro de clique (filteredData) use exatamente a mesma lógica dos gráficos.
  const matchConclusionOutcome = (value) => {
    if (value === null || value === undefined) return null;
    const n = String(value).trim().toLowerCase();
    if (!n) return null;
    if (n.includes('parcial') && n.includes('procedente')) return 'Parcialmente procedente';
    if (n.includes('improcedente')) return 'Improcedente';
    if (n.includes('procedente')) return 'Procedente';
    if (n.includes('inconclusiv')) return 'Inconclusivo';
    if (n.includes('fora do escopo')) return 'Fora do escopo';
    return null; // não é um desfecho reconhecido
  };
  const resolveConclusion = (complaint) => {
    const candidates = [complaint.status_conclusao, complaint.conclusao];
    if (complaint.admin_notes) {
      try {
        const notes = typeof complaint.admin_notes === 'string'
          ? (complaint.admin_notes.trim().startsWith('{') ? JSON.parse(complaint.admin_notes) : {})
          : complaint.admin_notes;
        candidates.push(notes.status_conclusao, notes.conclusao);
      } catch (e) { /* parse defensivo */ }
    }
    for (const candidate of candidates) {
      const outcome = matchConclusionOutcome(candidate);
      if (outcome) return outcome;
    }
    return 'Não informado';
  };

  // Função para processar dados reais em gráficos
  const processRealData = (complaints) => {
    if (!complaints || complaints.length === 0) return { 
      evolucao: [], tipos: [], departamentos: [], status: [], 
      anonimos: [], unidades: [], publico: [], conclusao: []
    };

    // Evolução temporal - últimos 12 meses
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: month.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        count: 0,
        resolved: 0,
        fullDate: month
      });
    }

    complaints.forEach(complaint => {
      const complaintDate = new Date(complaint.created_at);
      const monthKey = new Date(complaintDate.getFullYear(), complaintDate.getMonth(), 1);
      
      const monthIndex = months.findIndex(m => 
        m.fullDate.getTime() === monthKey.getTime()
      );
      
      if (monthIndex !== -1) {
        months[monthIndex].count++;
        if (complaint.status === 'resolved') {
          months[monthIndex].resolved++;
        }
      }
    });

    // Tipos de denúncia com legenda
    const tiposCount = {};
    complaints.forEach(complaint => {
      const tipo = complaint.responses?.tipo_denuncia || 'Outros';
      tiposCount[tipo] = (tiposCount[tipo] || 0) + 1;
    });
    const tipos = Object.entries(tiposCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 15) // Aumentei para 15 para mostrar mais tipos
      .map(([name, value], index) => ({
        name: name.length > 25 ? name.substring(0, 22) + '...' : name,
        fullName: name,
        value,
        color: tipoColors[index % tipoColors.length]
      }));

    // Departamentos (vertical agora)
    const deptCount = {};
    complaints.forEach(complaint => {
      const dept = complaint.responses?.departamento || 'Outros';
      deptCount[dept] = (deptCount[dept] || 0) + 1;
    });
    const departamentos = Object.entries(deptCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value], index) => ({
        name: name.length > 15 ? name.substring(0, 12) + '...' : name,
        fullName: name,
        value,
        color: index < 3 ? colors.primary[index] :
               index < 6 ? colors.secondary[index - 3] :
               colors.accent[index - 6] || colors.danger[0]
      }));

    // Status
    const statusMap = {
      'received': { name: 'Recebida', color: colors.primary[0] },
      'investigating': { name: 'Investigando', color: colors.accent[0] },
      'resolved': { name: 'Resolvida', color: colors.secondary[0] },
      'closed': { name: 'Fechada', color: colors.danger[0] }
    };

    const statusCount = {};
    complaints.forEach(complaint => {
      const status = complaint.status || 'received';
      const statusInfo = statusMap[status];
      if (statusInfo) {
        statusCount[statusInfo.name] = (statusCount[statusInfo.name] || 0) + 1;
      }
    });

    const status = Object.entries(statusCount).map(([name, value]) => {
      const statusEntry = Object.values(statusMap).find(s => s.name === name);
      return {
        name,
        value,
        color: statusEntry?.color || colors.primary[0]
      };
    });

    // Anônimos vs Identificados
    const anonimosCount = { 'Anônimas': 0, 'Identificadas': 0 };
    complaints.forEach(complaint => {
      const identificacao = complaint.responses?.identificacao;
      // Normalizar para minúsculo para comparar (considera "Não", "não", "NAO", etc.)
      const identificacaoLower = identificacao?.toLowerCase();
      if (identificacaoLower === 'não' || identificacaoLower === 'nao') {
        anonimosCount['Anônimas']++;
      } else if (identificacaoLower === 'sim') {
        anonimosCount['Identificadas']++;
      } else {
        anonimosCount['Anônimas']++; // Default para anônimas se não especificado
      }
    });

    const anonimos = [
      { name: 'Anônimas', value: anonimosCount['Anônimas'], color: colors.danger[0] },
      { name: 'Identificadas', value: anonimosCount['Identificadas'], color: colors.secondary[0] }
    ];

    // Unidades (local_ocorrencia)
    const unidadesCount = {};
    complaints.forEach(complaint => {
      const unidade = complaint.responses?.local_ocorrencia || 'Não informado';
      unidadesCount[unidade] = (unidadesCount[unidade] || 0) + 1;
    });
    const unidades = Object.entries(unidadesCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value], index) => ({
        name: name.length > 15 ? name.substring(0, 12) + '...' : name,
        fullName: name,
        value,
        color: colors.teal[index % 3] || colors.purple[index % 3] || colors.orange[index % 3]
      }));

    // Tipo de Público
    const publicoCount = {};
    complaints.forEach(complaint => {
      const publico = complaint.responses?.tipo_publico || 'Não informado';
      publicoCount[publico] = (publicoCount[publico] || 0) + 1;
    });
    const publico = Object.entries(publicoCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([name, value], index) => ({
        name: name.length > 20 ? name.substring(0, 17) + '...' : name,
        fullName: name,
        value,
        color: colors.pink[index % 3] || colors.purple[index % 3] || colors.orange[index % 3]
      }));

    // Conclusão (Grupo I) - distribuição dos desfechos (resolveConclusion definida
    // no escopo do componente, compartilhada com o filtro de clique).
    const conclusaoCount = {};
    complaints.forEach(complaint => {
      const outcome = resolveConclusion(complaint);
      conclusaoCount[outcome] = (conclusaoCount[outcome] || 0) + 1;
    });
    // Ordem/cores fixas por desfecho para uma leitura consistente do gráfico.
    const conclusionOrder = ['Procedente', 'Parcialmente procedente', 'Improcedente', 'Inconclusivo', 'Fora do escopo', 'Não informado'];
    const conclusionColorByName = {
      'Procedente': '#10b981',
      'Parcialmente procedente': '#f59e0b',
      'Improcedente': '#ef4444',
      'Inconclusivo': '#6366f1',
      'Fora do escopo': '#6b7280',
      'Não informado': '#cbd5e1'
    };
    const conclusao = Object.entries(conclusaoCount)
      .sort(([a], [b]) => conclusionOrder.indexOf(a) - conclusionOrder.indexOf(b))
      .map(([name, value]) => ({
        name,
        fullName: name,
        value,
        color: conclusionColorByName[name] || '#94a3b8'
      }));

    return { 
      evolucao: months, 
      tipos, 
      departamentos, 
      status, 
      anonimos,
      unidades, 
      publico,
      conclusao
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Construir URL com filtros
        const params = new URLSearchParams();
        if (selectedClient && selectedClient !== 'all') {
          params.append('client_slug', selectedClient);
        }
        
        const url = `https://reportfius-backend.up.railway.app/api/complaints/analytics/list${params.toString() ? '?' + params.toString() : ''}`;
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const complaints = (data.complaints || []).map(complaint => ({
            ...complaint,
            responses: parseResponses(complaint.responses)
          }));
          console.log('✅ Dados REAIS carregados:', complaints.length);
          setRealData(complaints);
        } else {
          console.warn('Erro na API');
          setRealData([]);
        }
      } catch (error) {
        console.warn('Erro ao conectar API:', error);
        setRealData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedClient]);

  // Resetar filtros quando o cliente muda
  useEffect(() => {
    setSelectedFilters({});
    console.log('🔄 Cliente alterado, resetando filtros');
  }, [selectedClient]);

  // Processar dados para gráficos
  const filteredData = useMemo(() => {
    console.log('🔄 Recalculando filteredData:', {
      realDataLength: realData.length,
      selectedFilters,
      filtersCount: Object.keys(selectedFilters || {}).length
    });

    if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
      console.log('✅ Sem filtros, retornando todos os dados');
      return realData;
    }

    const filtered = realData.filter(item => {
      return Object.entries(selectedFilters).every(([filterType, filterValue]) => {
        if (!filterValue) return true;

        if (filterType === 'departamento') {
          return (item.responses?.departamento || 'Outros') === filterValue;
        }
        if (filterType === 'tipo') {
          return (item.responses?.tipo_denuncia || 'Outros') === filterValue;
        }
        if (filterType === 'status') {
          const statusMap = { 
            'Recebida': 'received', 
            'Investigando': 'investigating', 
            'Resolvida': 'resolved', 
            'Fechada': 'closed' 
          };
          return item.status === statusMap[filterValue];
        }
        if (filterType === 'unidade') {
          return (item.responses?.local_ocorrencia || 'Não informado') === filterValue;
        }
        if (filterType === 'publico') {
          return (item.responses?.tipo_publico || 'Não informado') === filterValue;
        }
        if (filterType === 'conclusao') {
          return resolveConclusion(item) === filterValue;
        }
        if (filterType === 'anonimos') {
          const identificacao = item.responses?.identificacao;
          const identificacaoLower = identificacao?.toLowerCase();
          if (filterValue === 'Anônimas') {
            return identificacaoLower === 'não' || identificacaoLower === 'nao' || !identificacao;
          }
          return identificacaoLower === 'sim';
        }
        return true;
      });
    });

    console.log('✅ Filtragem concluída:', {
      original: realData.length,
      filtrado: filtered.length,
      filtros: selectedFilters
    });

    return filtered;
  }, [realData, selectedFilters]);

  // Processar dados para gráficos
  const chartData = useMemo(() => processRealData(filteredData), [filteredData]);

  // Debug: Verificar dados dos gráficos
  useEffect(() => {
    console.log('📊 chartData atualizado:', {
      tipos: chartData.tipos?.length || 0,
      anonimos: chartData.anonimos?.length || 0,
      departamentos: chartData.departamentos?.length || 0,
      status: chartData.status?.length || 0,
      unidades: chartData.unidades?.length || 0,
      publico: chartData.publico?.length || 0
    });
    console.log('📊 chartData.tipos:', chartData.tipos);
    console.log('📊 chartData.anonimos:', chartData.anonimos);
  }, [chartData]);

  // Função para filtrar dados
  const handleFilterClick = (filterType, filterValue) => {
    console.log('🔍 handleFilterClick chamado:', { filterType, filterValue });
    if (!filterValue) {
      console.warn('⚠️ filterValue está vazio');
      return;
    }

    setSelectedFilters(prev => {
      const currentValue = prev[filterType];
      const newFilters = { ...prev };

      if (currentValue === filterValue) {
        delete newFilters[filterType];
        console.log('✅ Removendo filtro:', filterType);
      } else {
        newFilters[filterType] = filterValue;
        console.log('✅ Aplicando filtro:', filterType, '=', filterValue);
      }

      console.log('📊 Filtros ativos:', newFilters);
      return newFilters;
    });
  };

  // Custom tooltip escuro
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          border: 'none',
          borderRadius: '8px',
          padding: '12px',
          color: 'white',
          fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          <p style={{ color: 'white', fontWeight: '600', marginBottom: '4px' }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ 
              color: 'white', 
              margin: '2px 0',
              fontSize: '13px'
            }}>
              {entry.name}: <span style={{ fontWeight: '600' }}>{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-700 text-lg">
            🔄 Carregando Dashboard Analítico...
          </div>
        </div>
      </div>
    );
  }

  // Calcular KPIs (alinhados com a lógica dos gráficos)
  const totalComplaints = filteredData.length;
  // Resolvidas = status 'closed' (conforme gráfico de status mostra "Fechada")
  const resolvedComplaints = filteredData.filter(c => c.status === 'closed').length;
  // Investigando = status 'investigating' (conforme kanban)
  const investigatingComplaints = filteredData.filter(c => c.status === 'investigating').length;
  // Taxa de resolução = denúncias fechadas / total
  const resolutionRate = totalComplaints > 0 ? Math.round((resolvedComplaints / totalComplaints) * 100) : 0;
  // Anônimas = quando identificacao é 'Não' ou 'nao' (normalizado, conforme gráfico de identificação)
  const anonymousComplaints = filteredData.filter(c => {
    const identificacao = c.responses?.identificacao;
    const identificacaoLower = identificacao?.toLowerCase();
    return identificacaoLower === 'não' || identificacaoLower === 'nao';
  }).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Dashboard Container - cresce com o conteúdo (sem rolagem interna nos gráficos) */}
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-200" style={{
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        
        {/* Header com KPIs e Filtro */}
        {/* `flex-wrap` nos três níveis: título + seletor + 5 KPIs não cabem numa
            linha só, e sem quebra a faixa vazava para fora da viewport (o título
            espremia em 3 linhas e o último KPI ficava cortado). */}
        <div className="bg-white border-b border-gray-200 p-6 flex flex-wrap gap-4 justify-between items-center flex-shrink-0">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 whitespace-nowrap">
              <span>🎯</span>
              Dashboard Analítico v2.0
            </h1>
            
            {/* Filtro de Cliente */}
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Cliente:</span>
              <SecureClientSelector
                selectedClient={selectedClient}
                onClientChange={(client) => setSelectedClient(client.client_slug)}
                className="w-48"
                showStats={false}
              />
            </div>
            
            {/* KPIs Horizontais */}
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Total', value: totalComplaints, color: 'text-blue-600', icon: '📊' },
                { label: 'Resolvidas', value: resolvedComplaints, color: 'text-green-600', icon: '✅' },
                { label: 'Investigando', value: investigatingComplaints, color: 'text-yellow-600', icon: '🔍' },
                { label: 'Taxa Resolução', value: `${resolutionRate}%`, color: 'text-purple-600', icon: '📈' },
                { label: 'Anônimas', value: anonymousComplaints, color: 'text-red-600', icon: '🕵️' }
              ].map((kpi, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-lg">{kpi.icon}</span>
                  <div>
                    <div className="text-xs text-gray-500 font-medium">
                      {kpi.label}
                    </div>
                    <div className={`text-lg font-bold ${kpi.color}`}>
                      {kpi.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Filtro Ativo */}
          {Object.keys(selectedFilters).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap max-w-md">
              {Object.entries(selectedFilters).map(([type, value]) => (
                <div key={`${type}-${value}`} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1 flex items-center gap-2 text-sm text-blue-600">
                  <span>{type}: {value}</span>
                  <button
                    onClick={() => handleFilterClick(type, value)}
                    className="text-red-500 hover:text-red-700 text-base leading-none"
                    aria-label={`Remover filtro ${type}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                onClick={() => setSelectedFilters({})}
                className="text-sm text-gray-600 hover:text-gray-800 underline ml-2"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Grade dos gráficos — ver styles/analytics-grid.css para por que as
            classes são `rf-` e não as utilitárias do Tailwind.
            Composição em 3 colunas: a série temporal ocupa 2 (precisa de eixo
            largo) e o resto ocupa 1. Dá 9 unidades em 3 linhas exatas. */}
        <div className="flex-1 rf-analytics-grid bg-gray-50">
          
          {/* Evolução Temporal - Span 2 colunas */}
          {/* `rf-span-2` só existe dentro das media queries: numa coluna só (mobile)
              o span não se aplica e não cria coluna implícita. */}
          <div className="rf-span-2 rf-analytics-card bg-white rounded-xl p-3 border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-300">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span>📈</span> Evolução Temporal
            </h3>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={chartData.evolucao}>
                <defs>
                  <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="month" 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#3b82f6" 
                  fillOpacity={1} 
                  fill="url(#colorArea)" 
                  name="Denúncias"
                  {...animationConfig}
                />
                <Area 
                  type="monotone" 
                  dataKey="resolved" 
                  stroke="#10b981" 
                  fill="#10b981" 
                  fillOpacity={0.3}
                  name="Resolvidas"
                  {...animationConfig}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Tipos de Denúncia - COM LEGENDA */}
          <div 
            className="glass-card hover-glow rf-analytics-card" 
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              padding: '0.8rem',
              border: '1px solid rgba(0,0,0,0.1)',
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🔄</span> Tipos
            </h3>
            <ResponsiveContainer 
              width="100%" 
              height="85%"
              style={{ pointerEvents: 'auto' }}
            >
              <PieChart
                style={{ pointerEvents: 'auto' }}
              >
                <Pie
                  data={chartData.tipos}
                  cx="50%"
                  cy="45%"
                  innerRadius={25}
                  outerRadius={60}
                  paddingAngle={1}
                  dataKey="value"
                  onClick={(entry, index, e) => {
                    console.log('🍰 Pie onClick - EVENTO CAPTURADO!');
                    console.log('🍰 Pie onClick - entry:', entry);
                    console.log('🍰 Pie onClick - index:', index);
                    console.log('🍰 Pie onClick - event:', e);
                    const value = entry?.fullName || entry?.name;
                    if (value) {
                      handleFilterClick('tipo', value);
                    }
                  }}
                  onMouseDown={(entry, index, e) => {
                    console.log('🖱️ Pie onMouseDown - EVENTO CAPTURADO!');
                    console.log('🖱️ Pie onMouseDown - entry:', entry);
                    const value = entry?.fullName || entry?.name;
                    if (value) {
                      handleFilterClick('tipo', value);
                    }
                  }}
                  {...animationConfig}
                >
                  {chartData.tipos.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      stroke="rgba(255,255,255,0.8)"
                      strokeWidth={1}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        console.log('🔵 Cell onClick - EVENTO CAPTURADO!');
                        console.log('🔵 Cell onClick - entry:', entry);
                        console.log('🔵 Cell onClick - index:', index);
                        console.log('🔵 Cell onClick - event:', e);
                        const value = entry?.fullName || entry?.name;
                        console.log('🔵 Cell onClick - valor extraído:', value);
                        if (value) {
                          handleFilterClick('tipo', value);
                        }
                      }}
                    />
                  ))}
                </Pie>
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconSize={8}
                  wrapperStyle={{
                    fontSize: '10px',
                    color: '#1a202c'
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Conclusão (Grupo I) - distribuição dos desfechos */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer',
            position: 'relative'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>✅</span> Conclusão
            </h3>
            <ResponsiveContainer
              width="100%"
              height="85%"
              style={{ pointerEvents: 'auto' }}
            >
              <PieChart
                style={{ pointerEvents: 'auto' }}
              >
                <Pie
                  data={chartData.conclusao}
                  cx="50%"
                  cy="45%"
                  innerRadius={25}
                  outerRadius={60}
                  paddingAngle={1}
                  dataKey="value"
                  onClick={(entry) => {
                    const value = entry?.fullName || entry?.name;
                    if (value) {
                      handleFilterClick('conclusao', value);
                    }
                  }}
                  onMouseDown={(entry) => {
                    const value = entry?.fullName || entry?.name;
                    if (value) {
                      handleFilterClick('conclusao', value);
                    }
                  }}
                  {...animationConfig}
                >
                  {chartData.conclusao.map((entry, index) => (
                    <Cell
                      key={`cell-conclusao-${index}`}
                      fill={entry.color}
                      stroke="rgba(255,255,255,0.8)"
                      strokeWidth={1}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const value = entry?.fullName || entry?.name;
                        if (value) {
                          handleFilterClick('conclusao', value);
                        }
                      }}
                    />
                  ))}
                </Pie>
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconSize={8}
                  wrapperStyle={{ fontSize: '10px', color: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Anônimos vs Identificados */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🕵️</span> Identificação
            </h3>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={chartData.anonimos}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    console.log('📊 Bar onClick (anonimos) - EVENTO CAPTURADO!');
                    console.log('📊 Bar onClick (anonimos) - entry completo:', entry);
                    const value = entry?.name || entry?.fullName;
                    console.log('📊 Bar onClick (anonimos) - valor extraído:', value);
                    if (value) {
                      handleFilterClick('anonimos', value);
                    } else {
                      console.error('❌ Bar onClick (anonimos) - Não foi possível extrair o valor! Entry:', entry);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  {...animationConfig}
                >
                  {chartData.anonimos.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Departamentos - VERTICAL */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🏢</span> Departamentos
            </h3>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={chartData.departamentos}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#1a202c" 
                  fontSize={9}
                  tick={{ fill: '#1a202c' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    console.log('📊 Bar onClick (departamento) - EVENTO CAPTURADO!');
                    console.log('📊 Bar onClick (departamento) - entry completo:', entry);
                    const value = entry?.fullName || entry?.name;
                    console.log('📊 Bar onClick (departamento) - valor extraído:', value);
                    if (value) {
                      handleFilterClick('departamento', value);
                    } else {
                      console.error('❌ Bar onClick (departamento) - Não foi possível extrair o valor! Entry:', entry);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  {...animationConfig}
                >
                  {chartData.departamentos.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Unidades (Local de Ocorrência) */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>📍</span> Unidades
            </h3>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={chartData.unidades}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#1a202c" 
                  fontSize={9}
                  tick={{ fill: '#1a202c' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    console.log('📊 Bar onClick (unidade) - EVENTO CAPTURADO!');
                    console.log('📊 Bar onClick (unidade) - entry completo:', entry);
                    const value = entry?.fullName || entry?.name;
                    console.log('📊 Bar onClick (unidade) - valor extraído:', value);
                    if (value) {
                      handleFilterClick('unidade', value);
                    } else {
                      console.error('❌ Bar onClick (unidade) - Não foi possível extrair o valor! Entry:', entry);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  {...animationConfig}
                >
                  {chartData.unidades.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tipo de Público */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>👥</span> Público
            </h3>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={chartData.publico}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#1a202c" 
                  fontSize={9}
                  tick={{ fill: '#1a202c' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    console.log('📊 Bar onClick (publico) - EVENTO CAPTURADO!');
                    console.log('📊 Bar onClick (publico) - entry completo:', entry);
                    const value = entry?.fullName || entry?.name;
                    console.log('📊 Bar onClick (publico) - valor extraído:', value);
                    if (value) {
                      handleFilterClick('publico', value);
                    } else {
                      console.error('❌ Bar onClick (publico) - Não foi possível extrair o valor! Entry:', entry);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  {...animationConfig}
                >
                  {chartData.publico.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Status das Denúncias */}
          <div className="glass-card hover-glow rf-analytics-card" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            padding: '0.8rem',
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1a202c',
              margin: '0 0 0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>📋</span> Status
            </h3>
            <ResponsiveContainer 
              width="100%" 
              height="85%"
              style={{ pointerEvents: 'auto' }}
            >
              <BarChart 
                data={chartData.status}
                style={{ pointerEvents: 'auto' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="name" 
                  stroke="#1a202c" 
                  fontSize={9}
                  tick={{ fill: '#1a202c' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#1a202c" 
                  fontSize={10}
                  tick={{ fill: '#1a202c' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    console.log('📊 Bar onClick (status) - EVENTO CAPTURADO!');
                    console.log('📊 Bar onClick (status) - entry completo:', entry);
                    const value = entry?.name || entry?.fullName;
                    console.log('📊 Bar onClick (status) - valor extraído:', value);
                    if (value) {
                      handleFilterClick('status', value);
                    } else {
                      console.error('❌ Bar onClick (status) - Não foi possível extrair o valor! Entry:', entry);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  {...animationConfig}
                >
                  {chartData.status.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tabela de Denúncias Filtradas - ABAIXO do Dashboard 16:9 */}
      <div className="max-w-7xl mx-auto mt-4 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span>📋</span> 
            Denúncias {Object.keys(selectedFilters).length > 0 ? 'Filtradas' : 'Recentes'} ({filteredData.length})
          </h3>
          {Object.keys(selectedFilters).length > 0 && (
            <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-md">
              {Object.entries(selectedFilters).map(([type, value]) => `${type}: ${value}`).join(' | ')}
            </div>
          )}
        </div>
        
        <div className="bg-white max-h-96 overflow-auto">
          <table className="w-full border-collapse">
            <thead className="bg-blue-50 sticky top-0 z-10">
              <tr>
                {['ID', 'Data', 'Tipo', 'Departamento', 'Unidade', 'Público', 'Status', 'Relato'].map(header => (
                  <th key={header} className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.slice(0, 50).map((complaint, index) => (
                <tr 
                  key={complaint.id || index}
                  style={{
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = 'rgba(59, 130, 246, 0.05)'}
                  onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                  onClick={() => {
                    setSelectedDetail({ type: 'complaint', data: complaint });
                    setShowDetails(true);
                  }}
                >
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                    #{complaint.id || index + 1}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#3b82f6' }}>
                    {formatDateOrDash(complaint.created_at)}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#059669', maxWidth: '120px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {complaint.responses?.tipo_denuncia || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#374151', maxWidth: '100px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {complaint.responses?.departamento || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', maxWidth: '100px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {complaint.responses?.local_ocorrencia || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', maxWidth: '80px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {complaint.responses?.tipo_publico || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      background: complaint.status === 'resolved' ? 'rgba(16, 185, 129, 0.1)' :
                                 complaint.status === 'investigating' ? 'rgba(245, 158, 11, 0.1)' :
                                 'rgba(59, 130, 246, 0.1)',
                      color: complaint.status === 'resolved' ? '#059669' :
                             complaint.status === 'investigating' ? '#d97706' :
                             '#3b82f6'
                    }}>
                      {getStatusLabel(complaint.status)}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', maxWidth: '200px' }}>
                    <div style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      maxWidth: '180px'
                    }}>
                      {complaint.responses?.relato_detalhado || 'Sem relato disponível'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredData.length === 0 && (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '1rem'
            }}>
              📭 Nenhuma denúncia encontrada com os filtros aplicados
            </div>
          )}
          
          {filteredData.length > 50 && (
            <div style={{
              padding: '1rem',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem',
              background: 'rgba(59, 130, 246, 0.05)',
              borderTop: '1px solid rgba(0,0,0,0.05)'
            }}>
              Mostrando primeiras 50 de {filteredData.length} denúncias
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {showDetails && selectedDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowDetails(false)}>
          <div className="glass-card" style={{
            background: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700', 
                color: '#1a202c',
                margin: 0 
              }}>
                📋 Detalhes da Denúncia {displayProtocolTag(selectedDetail.data)}
              </h3>
              <button 
                onClick={() => setShowDetails(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#6b7280',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            {selectedDetail.type === 'complaint' && (
              <div style={{ color: '#374151' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>ID:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {displayProtocolTag(selectedDetail.data)}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Data:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {new Date(selectedDetail.data.created_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Tipo:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {selectedDetail.data.responses?.tipo_denuncia || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Departamento:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {selectedDetail.data.responses?.departamento || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Unidade:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {selectedDetail.data.responses?.local_ocorrencia || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Público:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {selectedDetail.data.responses?.tipo_publico || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Status:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {getStatusLabel(selectedDetail.data.status)}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Identificação:</span>
                    <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.1rem' }}>
                      {selectedDetail.data.responses?.identificacao === 'Sim' ? 'Identificada' : 'Anônima'}
                    </div>
                  </div>
                </div>
                
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Relato Detalhado:</span>
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '1rem', 
                    background: 'rgba(248, 250, 252, 0.8)', 
                    borderRadius: '8px',
                    lineHeight: '1.6',
                    fontSize: '0.95rem',
                    color: '#374151'
                  }}>
                    {selectedDetail.data.responses?.relato_detalhado || 'Sem relato disponível'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsLegacyV2;
